"use client";

// The features array holds visual components as values and hands them to a
// Client Component, so this file has to sit on the client side of the boundary.

import SectionHeader from "@/components/ui/section-header";
import EnterpriseBanner from "@/components/ui/enterprise-banner";
import FeatureGrid, {
  type FeatureGridItem,
} from "@/components/sections/products/shared/feature-grid";
import {
  AccessPointsVisual,
  CarrierSignalVisual,
  CredentialsVisual,
  GeoTargetVisual,
  RotationVisual,
  SuccessRateVisual,
} from "@/components/sections/products/shared/feature-visuals";

const features: FeatureGridItem[] = [
  {
    Visual: CarrierSignalVisual,
    title: "Premium Mobile IPs",
    description:
      "Ethically sourced mobile IPs, protected by KYC, acceptable-use controls, and compliance-aware network operations.",
  },
  {
    Visual: GeoTargetVisual,
    title: "Precision Geo-targeting",
    description:
      "Target by country, city, or location, with Fastest options when you want the system to optimize automatically.",
  },
  {
    Visual: SuccessRateVisual,
    title: "Built for Request Success",
    description:
      "Premium pooled mobile IPs, location controls, and rotating or sticky sessions help reduce blocks and retries.",
  },
  {
    Visual: RotationVisual,
    title: "Rotating Mobile Proxies & Sticky Sessions",
    description:
      "Use random IP behavior for fresh exits, sticky sessions for continuity, or manual refresh whenever you need a new IP.",
  },
  {
    Visual: AccessPointsVisual,
    title: "Unlimited Access Points",
    description:
      "Create as many access points as your projects need, each backed by the shared mobile pool — spin up more any time.",
  },
  {
    Visual: CredentialsVisual,
    title: "Ready-to-copy Credentials",
    description:
      "Generate proxy lists, copy credentials, switch hostname formats, and test quickly with cURL examples.",
  },
];

export default function MobilePricingFeatures() {
  return (
    <section
      id="product-features"
      className="py-section border-border border-b"
      aria-labelledby="features-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Why Proxidize"
            id="features-heading"
            heading={<>Premium-grade. No compromises.</>}
            body="Everything you get with Proxidize mobile proxies — ethically sourced, KYC-verified, with the controls that matter."
          />
        </div>

        <FeatureGrid items={features} ariaLabel="Mobile proxy features" />

        <div className="mt-10">
          <EnterpriseBanner description="Custom rotation rules, priority support, higher volume limits, and custom pricing that may start at $0.50/GB above 10 TB, depending on volume and contract terms." />
        </div>
      </div>
    </section>
  );
}
